import { useEffect } from 'react';
import RunMiniMap from './RunMiniMap.jsx';
import { distanceMiles, pacePerMile, elevationFeet, stravaActivityUrl } from '../lib/format.js';

/** How many run boxes to show (multiple of 3 rows, doubled for the loop, capped). */
function getNumRunsShown(dataLen) {
  let numRunsShown = 2 * (dataLen - (dataLen % 3));
  if (numRunsShown > 30) numRunsShown = 30;
  return numRunsShown;
}

/** Maps the global slide index to the underlying activity index (matches setRowBoxes). */
function dataIndexFor(x, numRunsShown) {
  return (x % (numRunsShown / 6)) + Math.floor(x / (numRunsShown / 3)) * (numRunsShown / 6);
}

function RunBox({ activity, mapFirst }) {
  const map = <RunMiniMap activity={activity} />;
  const text = (
    <div className="runText">
      <div>{activity.name}</div>
      <div>{pacePerMile(activity)} min / mi</div>
      <div>{distanceMiles(activity)} mi</div>
      <div>{elevationFeet(activity)} ft</div>
    </div>
  );
  return (
    <div className="slide">
      <div className="runBoxOutline">
        <a href={stravaActivityUrl(activity)} target="_blank" rel="noreferrer">
          <div className="runBox">
            {mapFirst ? map : text}
            {mapFirst ? text : map}
          </div>
        </a>
      </div>
    </div>
  );
}

function Row({ data, numRunsShown, order, defaultDirection, extraClass }) {
  const start = (order * numRunsShown) / 3;
  const end = ((order + 1) * numRunsShown) / 3;
  const slides = [];
  for (let x = start; x < end; x++) {
    const di = dataIndexFor(x, numRunsShown);
    slides.push(<RunBox key={x} activity={data[di]} mapFirst={defaultDirection} />);
  }
  return (
    <div className={`slider${extraClass ? ' ' + extraClass : ''}`}>
      <div className={`slide-track${order === 1 ? ' slide-track2' : ''}`}>{slides}</div>
    </div>
  );
}

/**
 * The three-row infinite-scrolling carousel of recent runs on the front page.
 * Ported from getRecentRunsDisplay + setRowBoxes.
 */
export default function RecentRunsCarousel({ data }) {
  const numRunsShown = getNumRunsShown(data.length);

  useEffect(() => {
    document.documentElement.style.setProperty('--numRuns', String(numRunsShown / 6));
    document.documentElement.style.setProperty(
      '--rr-animation-duration',
      `${(numRunsShown / 6) * 5}s`,
    );
  }, [numRunsShown]);

  if (numRunsShown <= 0) return null;

  return (
    <div className="flexer">
      <Row data={data} numRunsShown={numRunsShown} order={0} defaultDirection />
      <Row data={data} numRunsShown={numRunsShown} order={1} defaultDirection={false} extraClass="slider2" />
      <Row data={data} numRunsShown={numRunsShown} order={2} defaultDirection extraClass="slider3" />
    </div>
  );
}
